/* eslint-disable max-lines */
import { EClustersType } from 'shared/map-search/types/clustersType';

import {
  POINT_RADIUS,
  DOT_SIZE_SMALL,
  DOT_SIZE_BIG,
  POINT_COLORS,
  REDESIGNED_POINT_RADIUS,
  POINT_BORDER_WIDTH,
  POINT_WITH_COUNT_RADIUS,
  POINT_WITH_COUNT_BORDER_WIDTH,
  POINT_COUNT_MAX_VALUE,
  POINT_COUNT_MIN_VALUE,
  POINT_GLOW_RADIUS,
  EXTENDED_POINT_BORDER_WIDTH,
  EXTENDED_POINT_RADIUS,
  POINT_PEAS_RADIUS,
} from '../../../constants/map';
import { TFeature } from '../../../types/map';

export enum FPointType {
  Regular = 1,
  ResidentialComplex = 2,
  Extended = 4,
  Viewed = 8,
  ResidentialComplexPeas = 16,
  ExtendedResidentialComplex = 32,
}

export interface IPointConfig {
  radius: number;
  color: string;
  textColor?: string;
  borderColor?: string;
  borderWidth?: number;
  count?: string;
  hasContourShadow: boolean;
  glowRadius?: number;
  glowPulsed?: boolean;
  glowPulsedColor: string;
  hideShadow?: boolean;
}

export interface IGetPointConfigParameters {
  feature: TFeature;
  isResidentialComplexesListing: boolean;
  isResultable: boolean;
  isDetailed: boolean;
  // @todo выпилить эксп CD-159979
  showOnlyPrice: boolean;
  pointType: EClustersType | null;
  isMobileView: boolean;
}

const COLORS_CONFIG_MAP: { [key: number]: Pick<IPointConfig, 'color' | 'borderColor' | 'textColor'> } = {
  [FPointType.Regular]: { color: '#0468ff', borderColor: '#fff' },
  [FPointType.Regular | FPointType.Viewed]: { color: '#83b4ff', borderColor: '#fff' },
  [FPointType.ResidentialComplex]: { color: '#3ac500', borderColor: '#fff' },
  [FPointType.ResidentialComplex | FPointType.Viewed]: { color: '#B0E899', borderColor: '#fff' },
  [FPointType.ResidentialComplexPeas]: { color: '#34AC0A', borderColor: '#fff' },
  [FPointType.ResidentialComplexPeas | FPointType.Viewed]: { color: '#B0E899', borderColor: '#fff' },
  [FPointType.Extended]: { color: '#fff', borderColor: '#0468ff', textColor: '#152242' },
  [FPointType.Extended | FPointType.Viewed]: { color: '#fff', borderColor: '#83b4ff', textColor: '#152242' },
  [FPointType.ExtendedResidentialComplex]: { color: '#fff', borderColor: '#3ac500', textColor: '#152242' },
  [FPointType.ExtendedResidentialComplex | FPointType.Viewed]: {
    color: '#fff',
    borderColor: '#3ac500',
    textColor: '#152242',
  },
};

// @todo выпилить эксп CD-159979
const POSUTOCHNO_EXP_POINT_CONFIG = {
  radius: 1,
  color: 'rgba(0,0,0,0)',
  textColor: 'rgba(0,0,0,0)',
  borderColor: 'rgba(0,0,0,0)',
  borderWidth: 1,
  count: '0',
  hasContourShadow: false,
  glowPulsedColor: '0, 0, 0',
};

function getPointType(feature: TFeature, isResidentialComplexesListing: boolean, isDetailed: boolean): FPointType {
  if (feature.properties.type === 'peas') {
    return FPointType.ResidentialComplexPeas;
  }

  if (feature.properties.hasNewbuildingsOnly && feature.properties.isExtended && !feature.properties.isActive) {
    return FPointType.ExtendedResidentialComplex;
  }

  if (feature.properties.hasNewbuildingsOnly && !isDetailed) {
    return FPointType.ResidentialComplex;
  }

  if (feature.properties.isExtended && !feature.properties.isActive) {
    return FPointType.Extended;
  }

  if (
    ((isResidentialComplexesListing || isDetailed || feature.properties.newbuilding?.isAnyFicheringPlus) &&
      feature.properties.hasNewobject) ||
    feature.properties.isAnyFromKp
  ) {
    return FPointType.ResidentialComplex;
  }

  return FPointType.Regular;
}

export function getPointConfig({
  feature,
  isResidentialComplexesListing,
  isResultable,
  isDetailed,
  // @todo выпилить эксп CD-159979
  showOnlyPrice,
  pointType: type,
  isMobileView,
}: IGetPointConfigParameters): IPointConfig {
  if (type) {
    return getPointConfigByType({ feature, type });
  }

  // @todo выпилить эксп CD-159979
  if (showOnlyPrice) {
    return POSUTOCHNO_EXP_POINT_CONFIG;
  }

  const pointType = getPointType(feature, isResidentialComplexesListing, isDetailed);

  const colors =
    COLORS_CONFIG_MAP[
      pointType | (feature.properties.isViewed && !feature.properties.isActive ? FPointType.Viewed : 0)
    ];

  const glowPulsed = feature.properties.isPulsedGlow && !feature.properties.isActive && !feature.properties.isViewed;

  const glowPulsedColor =
    pointType === FPointType.ResidentialComplex || pointType === FPointType.ResidentialComplexPeas
      ? '56, 188, 1'
      : '4, 104, 255';

  let glowRadius;
  if (
    feature.properties.isGlow &&
    (!feature.properties.favoriteIds || feature.properties.favoriteIds.length === 0) &&
    // @todo разработка в MNZ-700
    !feature.properties.isPulsedGlow
  ) {
    glowRadius = feature.properties.isActive ? 8 : POINT_RADIUS + 4;
  }

  let borderWidth = 1;
  if (isResultable && !isDetailed) {
    borderWidth = 2;
  } else if (isDetailed) {
    if (feature.properties.isExtended && !feature.properties.isActive) {
      borderWidth = 2;
    } else if (feature.properties.isGlow && !feature.properties.isActive) {
      borderWidth = 1;
    } else {
      borderWidth = 0;
    }
  }

  let radius = POINT_RADIUS;
  if (feature.properties.isActive) {
    radius = 3;
  } else {
    if (isResultable && !isDetailed) {
      if (feature.properties.count === 1) {
        radius = DOT_SIZE_SMALL;
      } else if (feature.properties.count > 1) {
        radius = DOT_SIZE_BIG;
      }
    }

    if (!isResultable && feature.properties.isExtended) {
      radius -= 1;
    }
  }

  if (pointType === FPointType.ResidentialComplexPeas) {
    radius = POINT_PEAS_RADIUS;
    borderWidth = 1;

    if (feature.properties.isActive) {
      glowRadius = 10;
    }
  }

  let count: string | undefined;
  if (isResultable && !isDetailed) {
    if (feature.properties.count > 1 && feature.properties.count <= 9) {
      count = String(feature.properties.count);
    } else if (feature.properties.count > 9) {
      count = '9+';
    }
  }

  const hideShadow = feature.properties.newbuilding?.isAnyFicheringPlus && isMobileView;

  return {
    ...colors,
    glowPulsed,
    glowPulsedColor,
    glowRadius,
    borderWidth,
    radius,
    count,
    hasContourShadow: isResultable && !isDetailed,
    hideShadow,
  };
}

interface IGetPointConfigByTypeParameters {
  feature: TFeature;
  type: EClustersType;
}

function getPointConfigByType(params: IGetPointConfigByTypeParameters): IPointConfig {
  const colorSettings = getPointColors(params);
  const radiusSettings = getPointRadius(params);
  const count = getPointCount(params);
  const glowRadius = getPointGlowRadius(params);
  const glowPulsed = getPointGlowPulsed(params);

  return {
    ...colorSettings,
    ...radiusSettings,
    count,
    glowRadius,
    glowPulsed,
    hasContourShadow: true,
  };
}

interface IGetPointColors {
  color: string;
  borderColor: string;
  textColor?: string;
  glowPulsedColor: string;
}

/**
 * @description
 * Получаем цвета пина в зависимости от свойств пина
 */
function getPointColors({ feature }: IGetPointConfigByTypeParameters): IGetPointColors {
  const { isActive, isExtended, isAnyFromKp, newbuilding, hasNewobject, isHovered } = feature.properties;

  const {
    white,
    lightBlue,
    regularBlue,
    hoveredBlue,
    activeBlue,
    lightGreen,
    regularGreen,
    hoveredGreen,
    activeGreen,
    textBlue,
    textGreen,
  } = POINT_COLORS;

  const isViewed = feature.properties.isViewed && !isActive;

  /**
   * @description Пины объявлений из новостроек и коттеджных поселков
   */
  if (isAnyFromKp || newbuilding?.isAnyFicheringPlus || hasNewobject) {
    /**
     * @description Пины рекомендованных объявлений
     */
    if (isExtended && !isActive) {
      return {
        color: white,
        borderColor: isViewed ? lightGreen : regularGreen,
        textColor: textGreen,
        glowPulsedColor: '0, 0, 0',
      };
    }

    let color = regularGreen;

    if (isViewed && !isActive) {
      color = lightGreen;
    } else if (isActive) {
      color = activeGreen;
    } else if (isHovered) {
      color = hoveredGreen;
    }

    return {
      color,
      borderColor: white,
      glowPulsedColor: '56, 188, 1',
    };
  }

  /**
   * @description Пины рекомендованных объявлений
   */
  if (isExtended && !isActive) {
    return {
      color: white,
      borderColor: isViewed ? lightBlue : regularBlue,
      textColor: textBlue,
      glowPulsedColor: '0, 0, 0',
    };
  }

  let color = regularBlue;

  if (isViewed && !isActive) {
    color = lightBlue;
  } else if (isActive) {
    color = activeBlue;
  } else if (isHovered) {
    color = hoveredBlue;
  }

  /** @description Пины остальных объявлений */
  return {
    color,
    borderColor: white,
    glowPulsedColor: '4, 104, 255',
  };
}

interface IGetPointRadius {
  radius: number;
  borderWidth: number;
}

function getPointRadius({ feature, type }: IGetPointConfigByTypeParameters): IGetPointRadius {
  const { count, isExtended } = feature.properties;

  if (count === 1 || type === EClustersType.Peas || type === EClustersType.Detailed) {
    return isExtended
      ? {
          radius: EXTENDED_POINT_RADIUS,
          borderWidth: EXTENDED_POINT_BORDER_WIDTH,
        }
      : {
          radius: REDESIGNED_POINT_RADIUS,
          borderWidth: POINT_BORDER_WIDTH,
        };
  }

  return {
    radius: POINT_WITH_COUNT_RADIUS,
    borderWidth: POINT_WITH_COUNT_BORDER_WIDTH,
  };
}

function getPointCount({ feature, type }: IGetPointConfigByTypeParameters): string | undefined {
  const { count } = feature.properties;

  if (type === EClustersType.Detailed || type === EClustersType.Peas || count < POINT_COUNT_MIN_VALUE) {
    return;
  }

  if (count > POINT_COUNT_MAX_VALUE) {
    return `${POINT_COUNT_MAX_VALUE}+`;
  }

  return String(count);
}

function getPointGlowRadius({ feature }: IGetPointConfigByTypeParameters): number | undefined {
  const { isGlow, isPulsedGlow, favoriteIds } = feature.properties;

  const hasFavorites = !!favoriteIds && favoriteIds.length > 0;

  return isGlow && !hasFavorites && !isPulsedGlow ? POINT_GLOW_RADIUS : undefined;
}

function getPointGlowPulsed({ feature }: IGetPointConfigByTypeParameters): boolean | undefined {
  const { isPulsedGlow, isActive, isViewed } = feature.properties;

  return isPulsedGlow && !isActive && !isViewed;
}
